import React, { useEffect, useState } from 'react';
import client from '../api/client';

function fmt(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Bar({ value, max, color = '#B71530' }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ background: '#e9ecef', borderRadius: '3px', height: '8px', minWidth: '60px' }}>
      <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: '3px', transition: 'width 0.3s' }} />
    </div>
  );
}

export default function AppAdminUsage() {
  const [usage, setUsage]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    client.get('/orgs/usage')
      .then(r => setUsage(r.data.usage || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const maxInvoices   = Math.max(1, ...usage.map(u => u.invoices));
  const maxEmails     = Math.max(1, ...usage.map(u => u.emails));
  const maxExceptions = Math.max(1, ...usage.map(u => u.exceptions));
  const maxBranding   = Math.max(1, ...usage.map(u => u.branding_bytes));

  return (
    <div>
      <h5 style={{ fontWeight: 600, fontSize: '16px', marginBottom: '16px' }}>Usage</h5>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-danger" /></div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card text-center" style={{ borderLeft: '4px solid #B71530' }}>
                <div className="card-body py-3">
                  <div style={{ fontSize: '26px', fontWeight: 700, color: '#B71530' }}>
                    {usage.length}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Organizations</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center" style={{ borderLeft: '4px solid #0d6efd' }}>
                <div className="card-body py-3">
                  <div style={{ fontSize: '26px', fontWeight: 700, color: '#0d6efd' }}>
                    {usage.reduce((s, u) => s + u.users, 0)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Total Users</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center" style={{ borderLeft: '4px solid #198754' }}>
                <div className="card-body py-3">
                  <div style={{ fontSize: '26px', fontWeight: 700, color: '#198754' }}>
                    {usage.reduce((s, u) => s + u.invoices, 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Total Invoices</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center" style={{ borderLeft: '4px solid #6c757d' }}>
                <div className="card-body py-3">
                  <div style={{ fontSize: '26px', fontWeight: 700, color: '#6c757d' }}>
                    {fmt(usage.reduce((s, u) => s + u.branding_bytes, 0))}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Branding Storage</div>
                </div>
              </div>
            </div>
          </div>

          {/* Per-org breakdown */}
          <div className="card">
            <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
              Per-Organisation Breakdown
            </div>
            <div className="card-body p-0">
              <table className="table table-sm mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '160px' }}>Organization</th>
                    <th>Users</th>
                    <th>Emails</th>
                    <th>Invoices</th>
                    <th>Exceptions</th>
                    <th>Branding</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.map(u => (
                    <tr key={u.org_id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{u.org_name}</div>
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>
                          <code>{u.subdomain}</code>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '13px', fontWeight: 500 }}>{u.users}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{u.emails.toLocaleString()}</div>
                        <Bar value={u.emails} max={maxEmails} color="#0d6efd" />
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{u.invoices.toLocaleString()}</div>
                        <Bar value={u.invoices} max={maxInvoices} color="#B71530" />
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{u.exceptions.toLocaleString()}</div>
                        <Bar value={u.exceptions} max={maxExceptions} color="#ffc107" />
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{fmt(u.branding_bytes)}</div>
                        <Bar value={u.branding_bytes} max={maxBranding} color="#6c757d" />
                      </td>
                    </tr>
                  ))}
                  {usage.length === 0 && (
                    <tr><td colSpan="6" className="text-center text-muted py-4">No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
